import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { BulkAPIResponse } from '@/types'
import type {
  QueryFolderMasterListModel,
  UpdateRecipientMasterListModel,
  UpdateRecipientMasterListParameter,
} from './model/recipientMaster'
import type {
  ClientBulkRegisterModel,
  ClientBulkRegisterParameter,
  ClientBulkRegisterResponseItem,
} from './model/client-model'
import type {
  QueryDestinationDetailListModel,
  UpdateRecipientDetailsModel,
} from '../send_cart/model/sendCartModel'
import type { FolderList } from '../model/folderModel'
import type {
  RecipientUserCSVExportParameter,
  RecipientUserCSVExportItem,
} from './model/recipient-user'

import { bffHTTP } from '@/utils/http/axios'
import { convertJSONToCSVFile, handleAPIException, handleBulkAPIException } from '@/utils'
import { errorMessage } from '@/utils/util'
import { getMessage } from '@/utils/message'
import { HTTPMethodsEnum } from '@/enums'
import { BasicResult } from '../model/resultModel'

enum Api {
  TransactionDetails_API = '/recipient_details',
  Recipents = '/recipents',
  recipentUserId = '/recipent_user_id',
}
/**
 * @description 送信先詳細の一覧を取得
 * @returns
 */
export async function getDetail(
  transactionSequence: number,
  params: any
): Promise<any | undefined> {
  try {
    const url: string = `${Api.TransactionDetails_API}/${transactionSequence}/paging`

    const response: AxiosResponse<QueryDestinationDetailListModel, any> = await bffHTTP({
      method: 'GET',
      url: url,
      params,
    })

    handleAPIException(response)
    return response
  } catch (error) {
    console.warn('getDetail', error)
  }
}

export async function getDetailAll(
  transactionSequence: number
): Promise<QueryDestinationDetailListModel | undefined> {
  try {
    const url: string = `${Api.TransactionDetails_API}/${transactionSequence}`

    const response: AxiosResponse<QueryDestinationDetailListModel, any> = await bffHTTP({
      method: 'GET',
      url: url,
      params: { sort_corporate_name: true },
    })

    handleAPIException(response)
    return response.data
  } catch (error) {
    console.warn('getDetail', error)
  }
}

/**
 * @description 送信先マスタ一覧更新 API
 * @param list 編集済みの送信先マスタ一覧
 * @returns 成功したか否か
 */
export async function updateRecipientMasterList(
  list: UpdateRecipientMasterListParameter
): Promise<boolean> {
  try {
    const response: AxiosResponse<UpdateRecipientMasterListModel, any> = await bffHTTP({
      url: '/recipients',
      method: 'PUT',
      data: list,
    })

    handleAPIException(response)

    if (response.status === 200) {
      errorMessage(getMessage('IF0001', ['送信マスタ', '保存']), 'success')
      return true
    }

    return false
  } catch (error) {
    console.warn('updateRecipientMasterList', error)
    return false
  }
}

/**
 * @description フォルダマスタ取得 API
 * @returns フォルダマスタリスト
 */
export async function queryFolderMasterList(): Promise<FolderList> {
  try {
    const response: AxiosResponse<QueryFolderMasterListModel, any> = await bffHTTP({
      method: HTTPMethodsEnum.GET,
      url: '/folders',
    })

    handleAPIException(response)
    return response.data.detail ?? []
  } catch (error) {
    console.warn('queryFolderMasterList', error)
    return []
  }
}

/**
 * @description 送信先マ詳細保存 API
 * @param list 送信先詳細
 * @returns 成功したか否か
 */
export async function updateRecipientDetail(
  list: UpdateRecipientDetailsModel
): Promise<boolean> {
  try {
    const response: AxiosResponse<null, any> = await bffHTTP({
      url: '/recipient_details',
      method: 'POST',
      data: list,
    })

    handleAPIException(response)

    if (response.status === 200) {
      errorMessage(getMessage('IF0001'), 'success')
      return true
    }

    return false
  } catch (error) {
    console.warn('updateRecipientDetail', error)
    return false
  }
}

/**
 * @descripiton 取引先一括登録 API
 * @param recipientId 送信先ID
 * @param list 取引先情報一覧
 * @returns 登録成功か否か
 */
export async function addMultiClient(
  recipientId: string,
  list: ClientBulkRegisterParameter
): Promise<boolean> {
  try {
    /** @descriptipn 一回の送信件数 */
    const ONE_TIME_LENGTH = 100

    let listForRegister: ClientBulkRegisterParameter = [...list]
    let failList: ClientBulkRegisterModel = []

    // 送信件数が多い時は送信回数を増やす
    while (listForRegister.length) {
      const response: AxiosResponse<BulkAPIResponse<ClientBulkRegisterModel>> =
        await bffHTTP(<
          AxiosRequestConfig<{ recipient_detail_list: ClientBulkRegisterParameter }>
        >{
          method: 'POST',
          url: `/recipient_details/multi`,
          data: {
            recipient_id: recipientId,
            recipient_detail_list: listForRegister.filter(
              (_, index: number): boolean => index < ONE_TIME_LENGTH
            ),
          },
          params: { holdLoading: listForRegister.length > ONE_TIME_LENGTH },
        })

      // 一括登録の業務エラー以外の例外が発生した場合は後続の登録を中断する
      if (response.status !== 200) {
        handleAPIException(response)
        return false
      }

      listForRegister = listForRegister.filter(
        (_, index: number): boolean => index >= ONE_TIME_LENGTH
      )

      failList = [...failList, ...response.data.detail]
    }

    const fakeResponse = <AxiosResponse<BulkAPIResponse<ClientBulkRegisterModel>>>{
      status: 200,
      data: {
        ok_count: -1,
        ng_count: failList.length,
        detail: failList,
      },
    }

    const [_, succeed] = handleBulkAPIException(fakeResponse)

    if (!succeed) {
      let csvHeader: string =
        '"法人番号","取引先コード","取引先名","送信先組織","担当者名","メールアドレス","エラー内容"'

      const errorLengthList: number[] = fakeResponse.data.detail.map<number>(
        (item) => item.error_message.length
      )

      // 一番目のエラーは`エラー内容`に記入されるので付ける空白の列の数は -1 が必要
      for (let i = 0; i < Math.max(...errorLengthList) - 1; i++) {
        csvHeader += ',""'
      }

      csvHeader += '\r\n'

      const formattedData: ClientBulkRegisterModel =
        fakeResponse.data.detail.map<ClientBulkRegisterResponseItem>((item) => {
          const nextErrors: { [property: string]: string } = {}

          for (let i = 0; i < item.error_message.length; i++) {
            if (i > 0) {
              nextErrors[`error_message_${i}`] = item.error_message[i]
            }
          }

          return {
            corporate_number_string: item.corporate_number_string,
            client_code: item.client_code,
            client_name: item.client_name,
            recipient_department_name: item.recipient_department_name,
            recipient_user_name: item.recipient_user_name,
            recipient_mail: item.recipient_mail,

            error_message: [item.error_message[0]],
            ...nextErrors,
          }
        })

      const now = new Date()
      const year: number = now.getFullYear()
      const month: string = `0${now.getMonth() + 1}`.slice(-2)
      const day: string = `0${now.getDate()}`.slice(-2)
      const hours: string = `0${now.getHours()}`.slice(-2)
      const minutes: string = `0${now.getMinutes()}`.slice(-2)
      const seconds: string = `0${now.getSeconds()}`.slice(-2)

      convertJSONToCSVFile({
        header: csvHeader,
        data: formattedData,
        fileName: `送信先担当者一括登録エラー_${year}${month}${day}${hours}${minutes}${seconds}`,
      })
    }

    return succeed
  } catch (error) {
    console.warn('addMultiUser', error)
    return false
  }
}

/**
 * @description 送信先利用者一覧出力API
 * @param recipient_id 送信先ID
 * @param corporate_name 法人名
 * @param client_name 取引先名
 * @param recipient_user_name 送信先利用者名
 * @returns  送信先利用者一覧出力変更用モデル
 */
export async function getRecipientUserCsv(
  recipient_id: number,
  corporate_name: string,
  client_name: string,
  recipient_user_name: string
): Promise<RecipientUserCSVExportParameter> {
  try {
    const response: AxiosResponse<
      BasicResult<RecipientUserCSVExportItem>,
      null
    > = await bffHTTP({
      method: 'GET',
      url: `/recipient_details/${recipient_id}/csv`,
      params: {
        corporate_name: corporate_name,
        client_name: client_name,
        recipient_user_name: recipient_user_name,
      },
    })

    handleAPIException(response)

    return formatRecipientUserCsv([...response.data.detail])
  } catch (error) {
    console.warn('getRecipientUserCsv', error)
    throw error
  }
}

/**
 * @description 送信先利用者一覧出力API結果フォーマットする
 * @param data 送信先利用者一覧出力API結果
 * @returns  送信先利用者一覧出力APIフォーマットされた結果
 */
function formatRecipientUserCsv(
  data: RecipientUserCSVExportParameter
): RecipientUserCSVExportParameter {
  return data.map<RecipientUserCSVExportItem>((item) => ({
    corporate_number: item.corporate_number,
    client_code: item.client_code,
    client_name: item.client_name,
    recipient_department_name: item.recipient_department_name,
    recipient_user_name: item.recipient_user_name,
    recipient_mail: item.recipient_mail,
  }))
}
